import type { FormAnswers, FormFieldView } from 'shared';

interface Props {
  fields: FormFieldView[];
  answers: FormAnswers;
  onChange: (key: string, value: FormAnswers[string]) => void;
  /** Read-only rendering, for the builder's preview. */
  disabled?: boolean;
}

/**
 * Renders one assessment version as inputs.
 *
 * Shared by the builder's preview and the donor microsite deliberately: if the preview
 * drew the questions differently from the real thing, the preview would be worth nothing.
 * The field definitions come from the server, so this maps types to controls and holds no
 * opinion about which questions exist.
 */
export function FormFieldsInput({ fields, answers, onChange, disabled }: Props) {
  return (
    <div className="stack" style={{ gap: '1rem' }}>
      {fields.map((f) => {
        const id = `q-${f.key}`;
        const labelId = `${id}-label`;
        const value = answers[f.key];
        // Checkbox and radio groups have no single control to point a <label> at, so they
        // get a labelled group instead — a `for` attribute aimed at nothing is worse than
        // no label, because a screen reader reports the question as unlabelled.
        const isGroup = f.type === 'multiselect'
          || (f.type === 'scale' && scalePoints(f.minValue, f.maxValue).length > 0);
        const caption = (
          <>
            {f.label}
            {f.required && <span style={{ color: 'var(--accent-strong)' }}> *</span>}
          </>
        );

        return (
          <div className="field" key={f.key}>
            {isGroup
              ? <span className="label" id={labelId}>{caption}</span>
              : <label className="label" id={labelId} htmlFor={id}>{caption}</label>}

            {f.type === 'textarea' ? (
              <textarea
                id={id} rows={3} disabled={disabled} required={f.required}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            ) : f.type === 'number' ? (
              <input
                id={id} type="number" inputMode="decimal" disabled={disabled} required={f.required}
                min={f.minValue ?? undefined} max={f.maxValue ?? undefined}
                value={typeof value === 'number' ? String(value) : ''}
                onChange={(e) => onChange(f.key, e.target.value === '' ? null : Number(e.target.value))}
              />
            ) : f.type === 'date' ? (
              <input
                id={id} type="date" disabled={disabled} required={f.required}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => onChange(f.key, e.target.value || null)}
              />
            ) : f.type === 'boolean' ? (
              <select
                id={id} disabled={disabled} required={f.required}
                value={value === true ? 'true' : value === false ? 'false' : ''}
                onChange={(e) => onChange(f.key, e.target.value === '' ? null : e.target.value === 'true')}
              >
                <option value="">— pilih —</option>
                <option value="true">Ya</option>
                <option value="false">Tidak</option>
              </select>
            ) : f.type === 'select' ? (
              <select
                id={id} disabled={disabled} required={f.required}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => onChange(f.key, e.target.value || null)}
              >
                <option value="">— pilih —</option>
                {(f.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : f.type === 'multiselect' ? (
              // A checkbox group rather than a multiple <select>: multi-select boxes are
              // notoriously easy to clear by accident with one stray click.
              <div className="check-group" role="group" aria-labelledby={labelId}>
                {(f.options ?? []).map((o) => {
                  const picked = Array.isArray(value) && value.includes(o.value);
                  return (
                    <label className="check" key={o.value}>
                      <input
                        type="checkbox" disabled={disabled} checked={picked}
                        onChange={(e) => {
                          const current = Array.isArray(value) ? value : [];
                          const next = e.target.checked
                            ? [...current, o.value]
                            : current.filter((v) => v !== o.value);
                          onChange(f.key, next.length === 0 ? null : next);
                        }}
                      />
                      <span>{o.label}</span>
                    </label>
                  );
                })}
              </div>
            ) : f.type === 'scale' ? (
              // A wide scale falls back to a number box. Rendering nothing would leave a
              // REQUIRED question with no way to answer it.
              scalePoints(f.minValue, f.maxValue).length > 0 ? (
                <div className="check-group" role="radiogroup" aria-labelledby={labelId}>
                  {scalePoints(f.minValue, f.maxValue).map((n) => (
                    <label className="check" key={n}>
                      <input
                        type="radio" name={id} disabled={disabled} checked={value === n}
                        onChange={() => onChange(f.key, n)}
                      />
                      <span className="mono">{n}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <input
                  id={id} type="number" inputMode="numeric" disabled={disabled} required={f.required}
                  min={f.minValue ?? undefined} max={f.maxValue ?? undefined}
                  value={typeof value === 'number' ? String(value) : ''}
                  onChange={(e) => onChange(f.key, e.target.value === '' ? null : Number(e.target.value))}
                />
              )
            ) : (
              <input
                id={id} disabled={disabled} required={f.required}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}

            {f.helpText && (
              <span className="muted" style={{ fontSize: 12.5 }}>{f.helpText}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The integer points of a scale. Capped at 11 buttons — a wider range is a number field
 * wearing a costume, and rendering 100 radios would be worse than an input box.
 */
function scalePoints(min: number | null, max: number | null): number[] {
  if (min === null || max === null || max <= min || max - min > 10) return [];
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}
